import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Star,
  MapPin,
  Globe,
  Mail,
  Phone,
  Shield,
  ExternalLink,
  Quote,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

import PublicLayout from "@/components/layout/PublicLayout";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const SITE_URL = "https://offertecheck.be";

export default function PartnerProfile() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/businesses/${slug}`)
      .then((r) => mounted && setPartner(r.data))
      .catch((e) => mounted && setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="oc-section">
          <div className="oc-container max-w-3xl text-center text-oc-textSecondary">Profiel laden…</div>
        </div>
      </PublicLayout>
    );
  }
  if (error === "not_found") return <Navigate to="/voor-bedrijven" replace />;
  if (error)
    return (
      <PublicLayout>
        <div className="oc-section">
          <div className="oc-container max-w-3xl text-center text-oc-textSecondary">
            Profiel kon niet geladen worden.{" "}
            <Link to="/" className="text-oc-secondary underline">
              Terug naar home
            </Link>
          </div>
        </div>
      </PublicLayout>
    );

  const rating = partner.google_rating;
  const reviewCount = partner.google_review_count || 0;
  const reviews = partner.google_reviews || [];
  const branches = partner.branches || [];
  const regions = partner.regions || [];

  // SEO
  const title = `${partner.business_name} — geverifieerd ${branches[0] || "OfferteExpert"} partner | OfferteExpert`;
  const description = `${partner.business_name} is een door OfferteExpert geverifieerde partner${
    rating ? ` met ${rating}/5 op ${reviewCount} Google-reviews` : ""
  }. Actief in ${branches.slice(0, 3).join(", ")} — ${regions.slice(0, 3).join(", ")}.`;

  // LocalBusiness JSON-LD
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: partner.business_name,
    description: partner.description,
    url: `${SITE_URL}/partner/${slug}`,
    ...(partner.website ? { sameAs: [partner.website] } : {}),
    ...(partner.google_formatted_address ? { address: partner.google_formatted_address } : {}),
    ...(rating
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: rating,
            reviewCount: reviewCount,
          },
        }
      : {}),
    ...(reviews.length
      ? {
          review: reviews.slice(0, 3).map((r) => ({
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
            author: { "@type": "Person", name: r.author_name },
            reviewBody: r.text,
          })),
        }
      : {}),
  };

  return (
    <PublicLayout>
      <Seo title={title} description={description} jsonLd={jsonLd} />

      <ProfileHero partner={partner} rating={rating} reviewCount={reviewCount} />

      <div className="oc-section bg-white">
        <div className="oc-container grid lg:grid-cols-3 gap-10">
          {/* Left column: about + reviews */}
          <div className="lg:col-span-2 space-y-12">
            <AboutSection partner={partner} branches={branches} regions={regions} />
            <ReviewsSection
              reviews={reviews}
              rating={rating}
              reviewCount={reviewCount}
              mapsUrl={partner.google_maps_url}
              partnerName={partner.business_name}
            />
          </div>

          {/* Right column: contact card */}
          <div>
            <ContactCard partner={partner} />
            <TrustCard />
          </div>
        </div>
      </div>

      <RelatedCta partner={partner} branches={branches} regions={regions} />
    </PublicLayout>
  );
}

/* ---------- Sections ---------- */
function ProfileHero({ partner, rating, reviewCount }) {
  return (
    <section className="relative oc-hero-gradient overflow-hidden text-white">
      <div className="absolute inset-0 oc-grid-bg opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#003B82]/65 via-[#003B82]/50 to-[#003B82]/85" />
      <div className="relative oc-container py-16 sm:py-20">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[12px] text-white/70 uppercase tracking-[0.12em] font-semibold">
          <Link to="/" className="hover:text-white">Home</Link>
          <span>›</span>
          <Link to="/voor-bedrijven" className="hover:text-white">Partners</Link>
          <span>›</span>
          <span className="text-white">{partner.business_name}</span>
        </nav>

        <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-400/20 backdrop-blur-md border border-emerald-400/40 text-[12px] uppercase tracking-[0.12em] font-semibold text-emerald-200">
          <Shield className="w-3.5 h-3.5" /> Geverifieerd partner
        </div>

        <h1 data-testid="partner-name" className="font-display font-extrabold text-[36px] sm:text-[52px] lg:text-[58px] leading-[1.05] tracking-tight mt-5 text-white">
          {partner.business_name}
        </h1>

        {rating && (
          <div className="mt-5 flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-2">
              <Stars rating={rating} size="lg" />
              <span className="font-mono text-[18px] font-semibold text-white">{rating.toFixed(1)}</span>
              <span className="text-white/70 text-[14px]">/ 5</span>
            </div>
            <span className="text-white/70 text-[14px]">{reviewCount} Google-reviews</span>
            {partner.google_maps_url && (
              <a
                href={partner.google_maps_url}
                target="_blank"
                rel="noreferrer"
                className="text-white/85 hover:text-white text-[13px] inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
              >
                Bekijk op Google Maps <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function AboutSection({ partner, branches, regions }) {
  return (
    <div data-testid="partner-about">
      <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
        Over deze partner
      </span>
      <h2 className="font-display font-bold text-[28px] sm:text-[34px] leading-tight tracking-tight text-oc-primary mt-3">
        Wat doet {partner.business_name}?
      </h2>
      <p className="mt-5 text-oc-textSecondary text-[16px] leading-relaxed whitespace-pre-line">
        {partner.description}
      </p>

      {branches.length > 0 && (
        <div className="mt-8">
          <h3 className="text-[14px] uppercase tracking-[0.08em] font-semibold text-oc-textSecondary">
            Branches
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {branches.map((b) => (
              <span
                key={b}
                className="px-3 py-1.5 rounded-oc-btn bg-oc-subtle text-oc-primary text-[13px] font-semibold border border-oc-border"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      )}

      {regions.length > 0 && (
        <div className="mt-6">
          <h3 className="text-[14px] uppercase tracking-[0.08em] font-semibold text-oc-textSecondary">
            Actief in
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {regions.map((r) => (
              <span
                key={r}
                className="px-3 py-1.5 rounded-oc-btn bg-white text-oc-textSecondary text-[13px] border border-oc-border inline-flex items-center gap-1.5"
              >
                <MapPin className="w-3 h-3" /> {r}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsSection({ reviews, rating, reviewCount, mapsUrl, partnerName }) {
  if (!reviews.length) {
    return (
      <div data-testid="partner-reviews">
        <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
          Google reviews
        </span>
        <h2 className="font-display font-bold text-[28px] sm:text-[34px] leading-tight tracking-tight text-oc-primary mt-3">
          Reviews worden geladen…
        </h2>
        <p className="mt-3 text-oc-textSecondary text-[15px] leading-relaxed">
          Nog geen reviews beschikbaar voor {partnerName}. Bekijk{" "}
          {mapsUrl ? (
            <a className="text-oc-secondary underline" href={mapsUrl} target="_blank" rel="noreferrer">
              het Google-profiel
            </a>
          ) : (
            "het Google-profiel"
          )}{" "}
          voor de meest actuele informatie.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="partner-reviews">
      <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
        Wat klanten zeggen op Google
      </span>
      <h2 className="font-display font-bold text-[28px] sm:text-[34px] leading-tight tracking-tight text-oc-primary mt-3">
        {reviewCount} Google-reviews · {rating.toFixed(1)}/5
      </h2>
      <p className="mt-3 text-oc-textSecondary text-[14px]">
        Hieronder een willekeurige selectie uit de meest recente reviews.
      </p>
      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        {reviews.map((r, i) => (
          <article
            key={i}
            className="p-6 bg-white border border-oc-border rounded-oc hover:shadow-oc-card-hover transition-shadow"
            style={{ animation: `fade-in-up 400ms ease-out both ${i * 80}ms` }}
          >
            <Quote className="w-5 h-5 text-oc-subtle" strokeWidth={1.5} />
            <p className="mt-3 text-oc-primary text-[15px] leading-relaxed">{(r.text || "").slice(0, 320)}</p>
            <div className="mt-5 flex items-center gap-3 pt-4 border-t border-oc-border">
              <div className="w-9 h-9 rounded-full bg-oc-subtle flex items-center justify-center text-oc-primary font-display font-semibold text-[13px]">
                {(r.author_name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-oc-primary truncate">{r.author_name}</p>
                <p className="text-oc-textSecondary text-[12px]">{r.relative_time_description}</p>
              </div>
              <Stars rating={r.rating} size="sm" />
            </div>
          </article>
        ))}
      </div>
      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-8 inline-flex items-center gap-2 text-oc-secondary hover:text-oc-primary font-semibold text-[14px] underline-offset-4 hover:underline"
        >
          Alle {reviewCount} reviews bekijken op Google <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function ContactCard({ partner }) {
  return (
    <div data-testid="partner-contact" className="bg-white border border-oc-border rounded-oc p-7 sticky top-24">
      <h3 className="font-display font-bold text-[20px] text-oc-primary">Contact</h3>
      <p className="mt-2 text-oc-textSecondary text-[14px]">Vraag direct een offerte aan bij deze partner.</p>

      <div className="mt-6 space-y-3">
        {partner.website && (
          <a
            href={partner.website.startsWith("http") ? partner.website : `https://${partner.website}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 p-3 rounded-oc-btn bg-oc-subtle hover:bg-oc-primary hover:text-white transition-colors group"
          >
            <Globe className="w-4 h-4" />
            <span className="text-[14px] font-semibold truncate">Bezoek website</span>
            <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-60 group-hover:opacity-100" />
          </a>
        )}
        {partner.contact_email && (
          <a
            href={`mailto:${partner.contact_email}?subject=Offerte-aanvraag via OfferteExpert`}
            className="flex items-center gap-3 p-3 rounded-oc-btn bg-oc-subtle hover:bg-oc-primary hover:text-white transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span className="text-[14px] font-semibold truncate">{partner.contact_email}</span>
          </a>
        )}
        {partner.contact_phone && (
          <a
            href={`tel:${partner.contact_phone.replace(/\s/g, "")}`}
            className="flex items-center gap-3 p-3 rounded-oc-btn bg-oc-subtle hover:bg-oc-primary hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="text-[14px] font-semibold">{partner.contact_phone}</span>
          </a>
        )}
        {partner.google_maps_url && (
          <a
            href={partner.google_maps_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 p-3 rounded-oc-btn bg-oc-subtle hover:bg-oc-primary hover:text-white transition-colors group"
          >
            <MapPin className="w-4 h-4" />
            <span className="text-[14px] font-semibold truncate">Google Maps</span>
            <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-60 group-hover:opacity-100" />
          </a>
        )}
      </div>

      {partner.google_formatted_address && (
        <p className="mt-6 text-[13px] text-oc-textSecondary leading-relaxed border-t border-oc-border pt-4">
          {partner.google_formatted_address}
        </p>
      )}
    </div>
  );
}

function TrustCard() {
  return (
    <div className="mt-5 p-6 bg-emerald-50 border border-emerald-200 rounded-oc">
      <Shield className="w-5 h-5 text-emerald-700" />
      <h4 className="mt-3 font-display font-bold text-[15px] text-emerald-900">
        Waarom geverifieerd?
      </h4>
      <ul className="mt-2 space-y-1.5 text-[13px] text-emerald-900/85 leading-relaxed">
        <li className="flex items-start gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700" />
          BTW-nummer gecontroleerd
        </li>
        <li className="flex items-start gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700" />
          Aangesloten bij OfferteExpert
        </li>
        <li className="flex items-start gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700" />
          Google-reviews live ingelezen
        </li>
      </ul>
    </div>
  );
}

function RelatedCta({ partner, branches }) {
  // Pre-fill the analyse wizard with this partner's primary branche
  const primaryBranche = branches[0] || "";
  const link = primaryBranche
    ? `/analyse?type=${encodeURIComponent(primaryBranche)}`
    : "/analyse";
  return (
    <section className="bg-oc-subtle border-t border-oc-border py-16">
      <div className="oc-container max-w-3xl text-center">
        <h3 className="font-display font-bold text-[24px] sm:text-[28px] text-oc-primary">
          Twijfel je over hun prijs?
        </h3>
        <p className="mt-3 text-oc-textSecondary text-[15px] leading-relaxed">
          Upload de offerte van {partner.business_name} en wij vergelijken elke post met
          actuele markttarieven — vanaf € 4,99.
        </p>
        <Link to={link}>
          <Button
            data-testid="partner-cta-analyse"
            className="mt-6 group bg-oc-primary text-white hover:bg-oc-secondary transition-all rounded-oc-btn px-7 h-13 text-[14px] font-semibold"
          >
            Check offerte snel & eerlijk <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

/* ---------- Atoms ---------- */
function Stars({ rating, size = "md" }) {
  const full = Math.round(rating);
  const sizes = { sm: "w-3 h-3", md: "w-4 h-4", lg: "w-5 h-5" };
  return (
    <div className="inline-flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`${sizes[size]} ${i < full ? "text-amber-500 fill-amber-500" : "text-white/30"}`}
        />
      ))}
    </div>
  );
}
